import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Mic, Music, Play, Square, Trash2, Video, X } from 'lucide-react';

const MAX_RECORDING_SECONDS = 600; // 10 minutes

// Bitrates are capped so a full-length recording stays under the backend's size limits
// (backend/constants/diary.js): ~82MB of 100MB for video, ~9MB of 30MB for audio.
const RECORDER_OPTIONS = {
  video: { videoBitsPerSecond: 1_000_000, audioBitsPerSecond: 96_000 },
  audio: { audioBitsPerSecond: 128_000 },
};

const MEDIA_CONSTRAINTS = {
  video: { video: { width: { ideal: 1280 }, height: { ideal: 720 } }, audio: true },
  audio: { audio: true },
};

const formatClock = (totalSeconds) =>
  `${String(Math.floor(totalSeconds / 60)).padStart(2, '0')}:${String(totalSeconds % 60).padStart(2, '0')}`;
const MAX_CLOCK = formatClock(MAX_RECORDING_SECONDS);

// MediaRecorder reports types like "video/webm;codecs=vp8,opus". Only the base type is
// sent: the upload's multipart parser can't read an unquoted comma in the type and
// would treat the file as text/plain, and the backend only needs the base type anyway.
const baseMimeType = (mimeType, kind) =>
  (mimeType || '').split(';')[0].trim().toLowerCase() || `${kind}/webm`;

const extensionFor = (mimeType, kind) => {
  if (mimeType.includes('mp4')) return kind === 'audio' ? 'm4a' : 'mp4';
  if (mimeType.includes('ogg')) return 'ogg';
  return 'webm';
};

const describeMediaError = (error, kind) => {
  const device = kind === 'video' ? 'camera and microphone' : 'microphone';
  if (error && (error.name === 'NotAllowedError' || error.name === 'SecurityError')) {
    return `Please allow the ${device} so you can record.`;
  }
  if (error && (error.name === 'NotFoundError' || error.name === 'OverconstrainedError')) {
    return `We couldn't find a ${device}.`;
  }
  return 'Could not access camera/microphone. Please check permissions.';
};

/**
 * Records from the camera / microphone. `onRecorded(file)` gets a File when a
 * recording finishes on its own terms (Stop, or hitting the time limit) - never
 * when it's cancelled. The camera / mic is always released again: on stop, on
 * cancel, and if the panel goes away mid-recording.
 */
function useMediaRecorder(kind, onRecorded) {
  const [status, setStatus] = useState('idle'); // 'idle' | 'starting' | 'recording'
  const [seconds, setSeconds] = useState(0);
  const [stream, setStream] = useState(null);

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);
  const startingRef = useRef(false);
  const discardRef = useRef(false);
  const unmountedRef = useRef(false);
  const onRecordedRef = useRef(onRecorded);

  useEffect(() => {
    onRecordedRef.current = onRecorded;
  });

  const releaseStream = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setStream(null);
  }, []);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state === 'recording') recorder.stop();
  }, []);

  const cancel = useCallback(() => {
    discardRef.current = true;
    stop();
  }, [stop]);

  const start = useCallback(async () => {
    if (startingRef.current || recorderRef.current) return;

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.error("Recording isn't supported in this browser.");
      return;
    }

    startingRef.current = true;
    setStatus('starting');
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia(MEDIA_CONSTRAINTS[kind]);

      // Permission can be granted after the child already left this tab - don't leave the camera on.
      if (unmountedRef.current) {
        mediaStream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = mediaStream;
      setStream(mediaStream);

      const recorder = new MediaRecorder(mediaStream, RECORDER_OPTIONS[kind]);
      chunksRef.current = [];
      discardRef.current = false;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const chunks = chunksRef.current;
        const discard = discardRef.current;
        const type = baseMimeType(recorder.mimeType, kind);

        releaseStream();
        recorderRef.current = null;
        startingRef.current = false;
        setStatus('idle');
        setSeconds(0);

        if (!discard && chunks.length > 0) {
          const file = new File(chunks, `${kind}-recording-${Date.now()}.${extensionFor(type, kind)}`, { type });
          onRecordedRef.current(file);
        }
      };

      recorderRef.current = recorder;
      startedAtRef.current = Date.now();
      recorder.start(1000);
      setSeconds(0);
      setStatus('recording');

      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startedAtRef.current) / 1000);
        setSeconds(Math.min(elapsed, MAX_RECORDING_SECONDS));
        if (elapsed >= MAX_RECORDING_SECONDS) {
          toast('You reached the 10 minute limit, so we stopped recording.', { icon: '⏱️' });
          stop();
        }
      }, 250);
    } catch (error) {
      releaseStream();
      recorderRef.current = null;
      startingRef.current = false;
      setStatus('idle');
      if (!unmountedRef.current) toast.error(describeMediaError(error, kind));
    }
  }, [kind, releaseStream, stop]);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      discardRef.current = true;
      const recorder = recorderRef.current;
      if (recorder && recorder.state === 'recording') recorder.stop();
      releaseStream();
    };
  }, [releaseStream]);

  return { status, seconds, stream, start, stop, cancel };
}

const COPY = {
  video: { title: 'Record Video', icon: Video, iconColor: 'text-violet-600' },
  audio: { title: 'Record Audio', icon: Music, iconColor: 'text-pink-500' },
};

function RecorderHeader({ kind }) {
  const { title, icon: Icon, iconColor } = COPY[kind];
  return (
    <div className="flex items-center gap-4">
      <Icon size={44} strokeWidth={2.25} fill="currentColor" className={`shrink-0 ${iconColor}`} aria-hidden="true" />
      <div>
        <h3 className="text-2xl font-extrabold text-slate-900">{title}</h3>
        <p className="text-sm font-medium text-slate-600">Record your thoughts, ideas or anything you want to share!</p>
      </div>
    </div>
  );
}

const WAVE_BARS = [10, 18, 28, 14, 34, 22, 12, 30, 16, 8];

// Decorative only: it pulses while recording so the mic feels alive.
function Waveform({ side, active }) {
  const bars = side === 'left' ? WAVE_BARS : [...WAVE_BARS].reverse();
  return (
    <div aria-hidden="true" className="flex min-w-0 flex-1 items-center justify-center gap-1.5 overflow-hidden">
      {bars.map((height, index) => (
        <span
          key={index}
          className={`w-1.5 shrink-0 rounded-full bg-pink-400 ${active ? 'animate-pulse' : ''}`}
          style={{
            height,
            opacity: side === 'left' ? 0.2 + index * 0.07 : 0.9 - index * 0.07,
            animationDelay: `${index * 80}ms`,
          }}
        />
      ))}
    </div>
  );
}

const SECONDARY_BUTTON =
  'flex h-12 items-center justify-center gap-2 rounded-xl bg-gray-100 px-4 font-extrabold text-slate-700 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-gray-100';

/**
 * The Video / Audio tabs of the Diary: record straight from the camera or
 * microphone, then Publish (the recording is `file`). The File itself lives in
 * DiaryZone so it survives flipping to "My Creations" and back; `onRecordingChange`
 * tells DiaryZone when a recording is in progress so it can hold Publish until you
 * press Stop.
 */
export default function DiaryRecorder({ kind, file, onFileChange, onRecordingChange }) {
  const isVideo = kind === 'video';
  const { status, seconds, stream, start, stop, cancel } = useMediaRecorder(kind, onFileChange);
  const isRecording = status === 'recording';
  const isBusy = status !== 'idle';

  const [previewUrl, setPreviewUrl] = useState(null);
  const liveVideoRef = useRef(null);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (liveVideoRef.current) liveVideoRef.current.srcObject = stream;
  }, [stream]);

  useEffect(() => {
    onRecordingChange(isBusy);
    return () => onRecordingChange(false);
  }, [isBusy, onRecordingChange]);

  const hasClip = Boolean(previewUrl);
  const handleMainClick = isRecording ? stop : start;
  const mainDisabled = status === 'starting' || (hasClip && !isRecording);
  const mainLabel = status === 'starting' ? 'Starting...' : isRecording ? 'Stop' : 'Record';
  const mainHint = hasClip && !isRecording ? 'Delete your recording to record again' : undefined;
  const handleDelete = () => onFileChange(null);

  if (!isVideo) {
    return (
      <div className="flex flex-col gap-4">
        <RecorderHeader kind={kind} />

        <div className="flex flex-col items-center gap-3 rounded-2xl border border-gray-100 bg-slate-50 px-4 py-6">
          <div className="flex w-full items-center justify-center gap-2">
            <Waveform side="left" active={isRecording} />
            <button
              type="button"
              onClick={handleMainClick}
              disabled={mainDisabled}
              aria-label={isRecording ? 'Stop recording' : 'Start recording'}
              title={mainHint}
              className={`m-3.5 flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-pink-500 text-white ring-[14px] ring-pink-100 transition-colors hover:bg-pink-600 disabled:cursor-not-allowed disabled:opacity-50 ${
                isRecording ? 'animate-pulse' : ''
              }`}
            >
              {isRecording ? <Square size={34} fill="currentColor" /> : <Mic size={42} strokeWidth={2.25} />}
            </button>
            <Waveform side="right" active={isRecording} />
          </div>
          <p className="text-sm font-semibold text-slate-500 tabular-nums">
            {formatClock(seconds)} / {MAX_CLOCK}
          </p>
        </div>

        <div className="flex items-center gap-3 rounded-2xl bg-slate-100 px-4 py-3">
          {hasClip ? (
            <audio src={previewUrl} controls aria-label="Your recording" className="h-10 min-w-0 flex-1" />
          ) : (
            <div aria-hidden="true" className="flex min-w-0 flex-1 items-center gap-3 text-slate-400">
              <Play size={22} fill="currentColor" className="shrink-0" />
              <div className="relative h-1.5 flex-1 rounded-full bg-slate-300">
                <span className="absolute -top-1 left-0 h-3.5 w-3.5 rounded-full bg-blue-500" />
              </div>
              <span className="text-sm font-semibold tabular-nums">00:00 / 00:00</span>
            </div>
          )}
          <button
            type="button"
            onClick={handleDelete}
            disabled={!hasClip}
            aria-label="Delete recording"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-300 text-slate-500 transition-colors hover:bg-white hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-500"
          >
            <Trash2 size={20} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <RecorderHeader kind={kind} />

      <div>
        <div className="relative aspect-video overflow-hidden rounded-2xl bg-slate-900">
          <video
            ref={liveVideoRef}
            autoPlay
            muted
            playsInline
            aria-label="Live camera preview"
            className={`h-full w-full object-cover ${isRecording ? '' : 'hidden'}`}
          />

          {!isRecording && hasClip && (
            <video src={previewUrl} controls aria-label="Your recording" className="h-full w-full bg-black object-contain" />
          )}

          {!isRecording && !hasClip && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center text-slate-300">
              <Video size={40} strokeWidth={2} aria-hidden="true" />
              <p className="text-sm font-semibold">
                {status === 'starting' ? 'Starting your camera...' : 'Tap the red button to start recording'}
              </p>
            </div>
          )}

          {isRecording && (
            <div className="absolute right-3 top-3 flex items-center gap-2 rounded-lg bg-black/60 px-3 py-1.5 text-sm font-semibold tabular-nums text-white">
              <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-500" aria-hidden="true" />
              {formatClock(seconds)} / {MAX_CLOCK}
            </div>
          )}
        </div>

        {/* relative + z-10 so the round button overlapping the preview's bottom edge paints on top of the video */}
        <div className="relative z-10 grid grid-cols-[1fr_auto_1fr] items-start gap-3 px-1 pt-3">
          <button type="button" onClick={cancel} disabled={!isRecording} className={`${SECONDARY_BUTTON} justify-self-start`}>
            <X size={20} strokeWidth={2.75} className="text-red-500" aria-hidden="true" /> Cancel
          </button>

          {/* Overlaps the preview - except once there's a clip, when it would cover the player's seek bar */}
          <div className={`${hasClip ? '' : '-mt-11'} flex flex-col items-center gap-1`}>
            <button
              type="button"
              onClick={handleMainClick}
              disabled={mainDisabled}
              aria-label={isRecording ? 'Stop recording' : 'Start recording'}
              title={mainHint}
              className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-red-500 text-white shadow-lg ring-4 ring-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isRecording ? (
                <Square size={26} fill="currentColor" />
              ) : (
                <span className="h-7 w-7 rounded-full bg-white" aria-hidden="true" />
              )}
            </button>
            <span className="text-sm font-extrabold text-slate-700">{mainLabel}</span>
          </div>

          <button type="button" onClick={handleDelete} disabled={!hasClip} className={`${SECONDARY_BUTTON} justify-self-end`}>
            <Trash2 size={20} className="text-red-500" aria-hidden="true" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}
